import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { CaretRight, List, MagnifyingGlass, SignOut, User as UserIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navLink = ({ isActive }) =>
    `text-xs font-bold uppercase tracking-[0.2em] transition-colors ${isActive ? "text-primary" : "text-neutral-400 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 lg:px-12 h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="w-8 h-8 bg-primary flex items-center justify-center">
            <span className="font-display font-black text-black text-sm">W</span>
          </div>
          <span className="font-display font-black text-lg tracking-tighter">WHEELSMEET</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/events" className={navLink} data-testid="nav-events">Eventi</NavLink>
          <NavLink to="/map" className={navLink} data-testid="nav-map">Mappa</NavLink>
          <NavLink to="/categories" className={navLink} data-testid="nav-categories">Categorie</NavLink>
          <NavLink to="/submit" className={navLink} data-testid="nav-submit">Aggiungi</NavLink>
          <NavLink to="/premium" className={({isActive}) => `text-xs font-bold uppercase tracking-[0.2em] transition-colors ${isActive ? "text-primary" : "text-primary/80 hover:text-primary"}`} data-testid="nav-premium">Premium</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon" className="hidden md:inline-flex" data-testid="header-search-btn">
            <Link to="/events"><MagnifyingGlass size={18} weight="bold" /></Link>
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-primary/50 transition-colors rounded-sm">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 bg-primary/20 border border-primary/40 flex items-center justify-center rounded-full">
                      <UserIcon size={12} weight="bold" />
                    </div>
                  )}
                  <span className="hidden sm:inline text-xs font-bold uppercase tracking-wide max-w-[100px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border w-56">
                <DropdownMenuItem asChild>
                  <Link to="/profile" data-testid="menu-profile" className="cursor-pointer">
                    <UserIcon size={14} weight="bold" className="mr-2" /> Profilo
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/submit" data-testid="menu-submit" className="cursor-pointer">
                    <CaretRight size={14} weight="bold" className="mr-2" /> Aggiungi evento
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout" className="cursor-pointer text-primary">
                  <SignOut size={14} weight="bold" className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider rounded-sm text-xs px-4" data-testid="header-login-btn">
              <Link to="/login">Accedi</Link>
            </Button>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            <List size={22} weight="bold" />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {[["Eventi","/events"],["Mappa","/map"],["Categorie","/categories"],["Aggiungi","/submit"],["Premium","/premium"]].map(([label, to]) => (
              <NavLink key={to} to={to} onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-wider py-2">{label}</NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  return (
    <footer className="border-t border-white/10 mt-24 bg-black">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <span className="font-display font-black text-black text-sm">W</span>
            </div>
            <span className="font-display font-black text-lg tracking-tighter">WHEELSMEET</span>
          </div>
          <p className="text-sm text-neutral-400 max-w-md leading-relaxed mb-4">
            La piattaforma per appassionati di auto e moto. Scopri raduni, meetup ed eventi in tutta Europa.
          </p>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Ideazione, design e sviluppo di <span className="text-primary font-bold">Antoneta Enache</span>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-4">Esplora</div>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/events" className="hover:text-primary transition-colors">Eventi</Link></li>
            <li><Link to="/map" className="hover:text-primary transition-colors">Mappa</Link></li>
            <li><Link to="/categories" className="hover:text-primary transition-colors">Categorie</Link></li>
            <li><Link to="/premium" className="hover:text-primary transition-colors">Premium</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-4">Community</div>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/submit" className="hover:text-primary transition-colors">Aggiungi evento</Link></li>
            <li><Link to="/profile" className="hover:text-primary transition-colors">Profilo</Link></li>
            <li>
              <a
                href={`${API_URL}/api/ownership/certificate`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ownership-certificate-link"
                className="hover:text-primary transition-colors inline-flex items-center gap-1"
              >
                Certificato di proprietà
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 py-6">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 text-center text-xs text-neutral-400 tracking-wider">
          © 2026 <span className="font-bold text-white">Antoneta Enache</span>. All rights reserved.
        </div>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 text-center text-[10px] text-neutral-600 tracking-wider mt-2">
          WheelsMeet™ · Ideazione, concept, design e codice sorgente sono proprietà intellettuale esclusiva di Antoneta Enache · Riproduzione vietata
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
